import { createContext, useCallback, useRef } from "react"
import useRawPromise from "../hooks/useRawPromise"

const BridgeContext = createContext()
export default BridgeContext

// keeps a copy of data that should only need to be fetched once per session
export function BridgeProvider({ children }) {
    const bridges = useRef({})
    const pendingRequests = useRef({})
    const { get } = useRawPromise()

    const getBridge = useCallback(async (resource) => {

        if (bridges.current[resource]) return bridges.current[resource]

        if (!allowedEndpoints.includes(resource)) {
            console.error('Unrecognized resource, did not fetch')
            return
        }

        if (!pendingRequests.current[resource]) {

            const query = endpointQueryMap[resource]

            pendingRequests.current[resource] = (async () => {
                try {
                    const { data, error } = await get(resource, { query })
                    if (error) throw error

                    bridges.current[resource] = data
                    return data
                } catch (error) {
                    console.error(error)
                    throw error
                } finally {
                    delete pendingRequests.current[resource]
                }
            })()
        }

        return pendingRequests.current[resource]
    }, [get])

    const clearCache = useCallback(() => {
        bridges.current = {}
        pendingRequests.current = {}
    }, [])

    return (
        <BridgeContext.Provider value={{ getBridge, clearCache }}>
            {children}
        </BridgeContext.Provider>
    )
}

const allowedEndpoints = [
    '/api/current/frontend-bridge/activity-event-types',
    '/api/current/frontend-bridge/aeration-heive',
    '/api/current/frontend-bridge/aeration-spacing',
    '/api/current/frontend-bridge/aeration-types',
    '/api/current/frontend-bridge/biotreatment-event-treatment-methods',
    '/api/current/frontend-bridge/biotreatment-event-treatment-types',
    '/api/current/frontend-bridge/body-impact-zones',
    '/api/current/frontend-bridge/cleansing-types',
    '/api/current/frontend-bridge/directions',
    '/api/current/frontend-bridge/disease-types',
    '/api/current/frontend-bridge/event-types',
    '/api/current/frontend-bridge/fertiliser-additional-nutritions',
    '/api/current/frontend-bridge/fertiliser-types',
    '/api/current/frontend-bridge/fungicide-product-treatment-types',
    '/api/current/frontend-bridge/grooming-machines',
    '/api/current/frontend-bridge/grooming-types',
    '/api/current/frontend-bridge/heating-types',
    '/api/current/frontend-bridge/heatingTemperatures',
    '/api/current/frontend-bridge/injury-event-reason',
    '/api/current/frontend-bridge/injury-event-severities',
    '/api/current/frontend-bridge/injury-types',
    '/api/current/frontend-bridge/mowing-machine-types',
    '/api/current/frontend-bridge/mowing-techniques',
    '/api/current/frontend-bridge/seeding-species',
    '/api/current/frontend-bridge/seeding-types',
    '/api/current/frontend-bridge/sports',
    '/api/current/frontend-bridge/training-team-types',
    '/api/current/frontend-bridge/match-team-types',
    '/api/current/frontend-bridge/training-types',
    '/api/current/frontend-bridge/units',
    `/api/current/frontend-bridge/line-marking-methods`,
    '/api/current/frontend-bridge/top-dressing-materials',
    '/api/current/frontend-bridge/timezones',
    '/api/current/frontend-bridge/index-category-types',
    '/api/current/frontend-bridge/boot-types',
    '/api/current/frontend-bridge/watering-types',
    '/api/current/frontend-bridge/pitch-protection-drill-types',
    `/api/current/frontend-bridge/artificial-lighting-grid-sizes`,
    `/api/current/frontend-bridge/artificial-lighting-types`,

    // not strictly frontendbridges but are entities in backend that should not be configurable by user
    '/api/current/fungicide-products',
    '/api/current/injuries',
    '/api/current/nematode-products',
    `/api/current/frontend-bridge/cover-event-products`,
    `/api/current/frontend-bridge/cover-event-types`,
]

//always fetch theses with these queries:
const endpointQueryMap = {
    '/api/current/injuries': { pagination: false }
}
